import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icons.jsx';
import { notify } from '@/lib/notifications';
import { api } from '../../services/api/client.js';
import { BrandIdentityTab } from './BrandIdentityTab.jsx';
import { PublishingRulesTab } from './PublishingRulesTab.jsx';
import { StrategyOverviewTab } from './StrategyOverviewTab.jsx';
import { WeeklyPlanTab } from './WeeklyPlanTab.jsx';
import { AnalyticsTab } from './AnalyticsTab.jsx';
import { OverviewTab } from './OverviewTab.jsx';
import { ResearchTab } from './ResearchTab.jsx';
import {
  defaultBrandProfile,
  defaultPublishingPreferences,
  toBrandForm,
  toPublishingForm,
} from './shared/brandFormUtils.js';

// Research tab rollout flag — flip per-browser with
// localStorage.setItem('dathent.research.enabled', '1'). The research-first
// onboarding flag implies it (the Setup Journey links here). Read LIVE on
// every call: the SPA never reloads between login and here, so a module-load
// constant silently ignored flags set from the console mid-session.
function researchTabEnabled() {
  try {
    return typeof window !== 'undefined' && (
      window.localStorage.getItem('dathent.research.enabled') !== '0'
      || window.localStorage.getItem('dathent.research.onboarding') === '1'
    );
  } catch (_) {
    return false;
  }
}

const BASE_TABS = [
  { id: 'overview', label: 'Overview', icon: 'grid', hint: 'System health and next actions' },
  { id: 'brand', label: 'Brand inputs', icon: 'sparkles', hint: 'Minimal checklist, expand to edit' },
  { id: 'plan', label: 'Content Plan', icon: 'target', hint: 'Client-facing plan layer' },
  { id: 'month', label: 'Monthly plan', icon: 'calendar', hint: 'Four weeks, generated when needed' },
  { id: 'analytics', label: 'Performance', icon: 'flame', hint: 'Learn what is working' },
];

const RESEARCH_TAB = { id: 'research', label: 'Research', icon: 'search', hint: 'Citation-backed market research' };

function visibleTabs() {
  return researchTabEnabled() ? [RESEARCH_TAB, ...BASE_TABS] : BASE_TABS;
}

function normalizeTabId(value) {
  if (value === 'identity') return 'brand';
  if (value === 'strategy') return 'plan';
  if (value === 'weekly') return 'month';
  if (value === 'platforms') return 'plan';
  return visibleTabs().some((tab) => tab.id === value) ? value : 'overview';
}

export function ContentPlanScreen({ onAskAI }) {
  const { t } = useTranslation();
  const location = useLocation();
  const routerNavigate = useNavigate();

  const initialTab = useMemo(() => {
    const t = new URLSearchParams(location.search).get('tab');
    return normalizeTabId(t);
  }, [location.search]);

  const [tab, setTabState] = useState(initialTab);
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    const normalized = normalizeTabId(t);
    if (normalized !== tab) setTabState(normalized);
  }, [location.search, tab]);

  const goTab = (next) => {
    const normalized = normalizeTabId(next);
    setTabState(normalized);
    const params = new URLSearchParams(location.search);
    params.set('tab', normalized);
    routerNavigate({ pathname: '/app/plan', search: `?${params.toString()}` }, { replace: true });
  };

  // Slim cross-tab banner while a research run is active — without it the
  // plan screens gave no hint that the market research (and the strategy
  // derived from it) was still being generated.
  const [activeResearch, setActiveResearch] = useState(null);
  useEffect(() => {
    if (!researchTabEnabled()) return undefined;
    let cancelled = false;
    let timer = null;
    const ACTIVE = ['queued', 'gathering', 'analyzing', 'synthesizing', 'verifying'];
    const probe = async () => {
      try {
        const res = await api.research.list();
        const latest = ((res?.data ?? res)?.reports || [])[0] || null;
        if (cancelled) return;
        const isActive = latest && ACTIVE.includes(latest.status);
        setActiveResearch(isActive ? latest : null);
        if (isActive) {
          api.invalidateCache?.('/api/research');
          timer = setTimeout(probe, 20000);
        }
      } catch (_) {
        if (!cancelled) setActiveResearch(null);
      }
    };
    probe();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const [loading, setLoading] = useState(true);
  const [profileMissing, setProfileMissing] = useState(false);
  const [brandForm, setBrandForm] = useState(defaultBrandProfile());
  const [publishingForm, setPublishingForm] = useState(defaultPublishingPreferences());
  const [platformPrefs, setPlatformPrefs] = useState([]);
  const [platformRegistry, setPlatformRegistry] = useState({});
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [strategy, setStrategy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const [brandResult, publishingResult, platformResult, socialResult, registryResult, strategyResult] = await Promise.allSettled([
        api.profile.getBrandProfile(),
        api.profile.getPublishingPreferences(),
        api.profile.getPlatformPreferences(),
        api.socialAccounts.list(),
        api.platforms.registry(),
        api.strategy.get(),
      ]);
      if (cancelled) return;

      if (brandResult.status === 'fulfilled') {
        setBrandForm(toBrandForm(brandResult.value.brandProfile));
        setProfileMissing(false);
      } else if (brandResult.reason?.status === 404) {
        setProfileMissing(true);
      } else {
        notify('contentPlan.brandLoadFailed', { error: brandResult.reason });
      }

      if (publishingResult.status === 'fulfilled') {
        setPublishingForm(toPublishingForm(publishingResult.value.publishingPreferences));
      }
      if (platformResult.status === 'fulfilled') {
        setPlatformPrefs(platformResult.value.platformPreferences || []);
      }
      if (socialResult.status === 'fulfilled') {
        setSocialAccounts(socialResult.value.accounts || socialResult.value.socialAccounts || []);
      }
      if (registryResult.status === 'fulfilled') {
        setPlatformRegistry(Object.fromEntries((registryResult.value.platforms || []).map((item) => [normalizePlatform(item.platform), item])));
      }
      if (strategyResult.status === 'fulfilled') {
        setStrategy(strategyResult.value.strategy || null);
      }

      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (profileMissing) {
    return (
      <div className="page cp-page desktop-page-content">
        <div className="card ds-card-pad settings-card cp-missing-card">
          <div className="settings-card-header">
            <h3 className="settings-card-title">{t('contentPlanScreen.completeOnboardingFirst')}</h3>
            <p className="settings-card-description">
              {t('contentPlanScreen.brandProfileNotCreated')}
            </p>
          </div>
          <div className="settings-card-footer" style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}>
            <button className="btn btn-primary" onClick={() => routerNavigate('/onboarding')}>
              {t('contentPlanScreen.openOnboarding')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page cp-page desktop-page-content">
      <div className="cp-workbench-row">
        <nav className="cp-tab-strip" aria-label={t('contentPlanScreen.sectionsNavLabel')}>
          {visibleTabs().map((item) => (
            <button
              key={item.id}
              type="button"
              className={'cp-tab-button ' + (tab === item.id ? 'is-active' : '')}
              onClick={() => goTab(item.id)}
              aria-current={tab === item.id ? 'page' : undefined}
            >
              <Icon name={item.icon} size={15} />
              <span>{t(`contentPlanScreen.tab_${item.id}`, item.label)}</span>
            </button>
          ))}
        </nav>
        <div className="cp-workbench-actions">
          <button className="btn btn-secondary" onClick={() => onAskAI?.({ scope: 'content-plan', tab })}>
            <Icon name="sparkles" size={13} /> {t('contentPlanScreen.askAi')}
          </button>
          <button className="btn btn-primary" onClick={() => goTab('plan')}>
            <Icon name={strategy ? 'refresh' : 'target'} size={13} /> {strategy ? t('contentPlanScreen.reviewRegenerate') : t('contentPlanScreen.generatePlan')}
          </button>
        </div>
      </div>

      {activeResearch && tab !== 'research' && (
        <div className="cp-research-banner" role="status">
          <span className="cp-rb-dot" aria-hidden="true" />
          <span>{t('contentPlanScreen.researchRunningBanner')}</span>
          <button type="button" className="btn btn-soft btn-sm" onClick={() => goTab('research')}>
            {t('contentPlanScreen.researchRunningCta')}
          </button>
        </div>
      )}

      <main className="cp-main-panel">
        {loading ? (
          <LoadingPanel />
        ) : (
          <div className="cp-tab-content">
            {tab === 'research' && (
              <ResearchTab brandProfile={brandForm} />
            )}
            {tab === 'overview' && (
              <OverviewTab
                brandForm={brandForm}
                publishingForm={publishingForm}
                platformPrefs={platformPrefs}
                socialAccounts={socialAccounts}
                strategy={strategy}
                onJumpTo={goTab}
                onAskAI={onAskAI}
                onConnectPlatform={(platform) => api.socialAccounts.connect(platform, { returnTo: '/app/plan?tab=overview' })}
              />
            )}
            {tab === 'brand' && (
              <BrandIdentityTab
                form={brandForm}
                setForm={setBrandForm}
                onSaved={(brandProfile) => setBrandForm(toBrandForm(brandProfile))}
                onAskAI={onAskAI}
                socialAccounts={socialAccounts}
                onConnectPlatform={(platform) => api.socialAccounts.connect(platform)}
              />
            )}
            {tab === 'rules' && (
              <PublishingRulesTab
                publishingForm={publishingForm}
                setPublishingForm={setPublishingForm}
                platformPrefs={platformPrefs}
                setPlatformPrefs={setPlatformPrefs}
              />
            )}
            {tab === 'plan' && (
              <StrategyOverviewTab
                strategy={strategy}
                setStrategy={setStrategy}
                onJumpTo={goTab}
              />
            )}
            {tab === 'month' && (
              <WeeklyPlanTab
                strategy={strategy}
                platformPrefs={platformPrefs}
                platformRegistry={platformRegistry}
                socialAccounts={socialAccounts}
                publishingForm={publishingForm}
                onJumpTo={goTab}
              />
            )}
            {tab === 'analytics' && (
              <AnalyticsTab
                platformPrefs={platformPrefs}
                onJumpTo={goTab}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function normalizePlatform(platform) {
  return platform === 'twitter' ? 'x' : platform;
}

function LoadingPanel() {
  const { t } = useTranslation();
  return (
    <div className="card ds-card-pad settings-card">
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <Icon name="refresh" size={14} />
        <span className="muted">{t('contentPlanScreen.loadingContentPlan')}</span>
      </div>
    </div>
  );
}
